/**
 * GoogleSheetsPicker Component
 * 
 * Este componente integra Google Picker API para permitir a los usuarios
 * seleccionar hojas de cálculo de su Google Drive.
 * 
 * Con el scope drive.file (recomendado por Google para verificación OAuth),
 * los usuarios deben seleccionar explícitamente qué archivos compartir
 * con la aplicación usando este Picker.
 * 
 * Documentación:
 * - Google Picker API: https://developers.google.com/drive/picker/guides/overview
 * - drive.file scope: https://developers.google.com/drive/api/guides/api-specific-auth
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Link2, Loader2, FolderOpen } from 'lucide-react';
import { getGooglePickerConfig, PickerConfigResponse } from '@/lib/googleSheetsApi';

// Tipo para la configuración del Picker
type PickerConfig = PickerConfigResponse;

// Tipo para un archivo seleccionado del Picker
interface PickerDocument {
  id: string;
  name: string;
  url: string;
  mimeType: string;
  lastEditedUtc?: number;
  iconUrl?: string;
  description?: string;
  type?: string;
  serviceId?: string;
  spikeId?: string;
  supportLink?: string;
}

interface GoogleSheetsPickerProps {
  onSheetsSelected?: (sheets: PickerDocument[]) => void;
  onPickerOpened?: () => void;
  onPickerClosed?: () => void;
  buttonText?: string;
  buttonVariant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disabled?: boolean;
  showIcon?: boolean;
  multiSelect?: boolean;
  autoOpen?: boolean;
}

// Declaración global para Google Picker API
declare global {
  interface Window {
    google?: {
      picker?: {
        PickerBuilder: new () => GooglePickerBuilder;
        ViewId: {
          SPREADSHEETS: string;
          DOCS: string;
          FOLDERS: string;
        };
        Feature: {
          MULTISELECT_ENABLED: string;
          NAV_HIDDEN: string;
          SUPPORT_DRIVES: string;
        };
        Action: {
          PICKED: string;
          CANCEL: string;
        };
        DocsView: new (viewId?: string) => GoogleDocsView;
      };
    };
    gapi?: {
      load: (api: string, callback: () => void) => void;
      client?: {
        init: (config: object) => Promise<void>;
        load: (api: string, version: string) => Promise<void>;
      };
    };
  }
}

interface GooglePickerBuilder {
  addView: (view: GoogleDocsView) => GooglePickerBuilder;
  setOAuthToken: (token: string) => GooglePickerBuilder;
  setDeveloperKey: (key: string) => GooglePickerBuilder;
  setAppId: (appId: string) => GooglePickerBuilder;
  setCallback: (callback: (data: GooglePickerResponse) => void) => GooglePickerBuilder;
  enableFeature: (feature: string) => GooglePickerBuilder;
  setTitle: (title: string) => GooglePickerBuilder;
  setLocale: (locale: string) => GooglePickerBuilder;
  build: () => GooglePicker;
}

interface GoogleDocsView {
  setMimeTypes: (mimeTypes: string) => GoogleDocsView;
  setIncludeFolders: (include: boolean) => GoogleDocsView;
  setSelectFolderEnabled: (enabled: boolean) => GoogleDocsView;
  setMode: (mode: 'list' | 'grid') => GoogleDocsView;
}

interface GooglePicker {
  setVisible: (visible: boolean) => void;
}

interface GooglePickerResponse {
  action: string;
  docs?: PickerDocument[];
}

const GOOGLE_PICKER_SCRIPT_URL = 'https://apis.google.com/js/api.js';
const SPREADSHEET_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';

const GoogleSheetsPicker: React.FC<GoogleSheetsPickerProps> = ({
  onSheetsSelected,
  onPickerOpened,
  onPickerClosed,
  buttonText = 'Seleccionar Hojas de Google',
  buttonVariant = 'default',
  buttonSize = 'default',
  className = '',
  disabled = false,
  showIcon = true,
  multiSelect = true,
  autoOpen = false
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pickerConfig, setPickerConfig] = useState<PickerConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  // Cargar script de Google API
  const loadGoogleApi = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      // Si ya está cargado, resolver inmediatamente
      if (window.gapi) {
        resolve();
        return;
      }

      // Verificar si el script ya existe
      const existingScript = document.querySelector(`script[src="${GOOGLE_PICKER_SCRIPT_URL}"]`);
      if (existingScript) {
        // Esperar a que cargue
        const checkLoaded = setInterval(() => {
          if (window.gapi) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 100);
        return;
      }

      // Crear y cargar el script
      const script = document.createElement('script');
      script.src = GOOGLE_PICKER_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Error al cargar Google API'));
      };
      
      document.body.appendChild(script);
    });
  }, []);

  // Cargar librería del Picker
  const loadPickerLibrary = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (!window.gapi) {
        reject(new Error('Google API no disponible'));
        return;
      }

      if (window.google?.picker) {
        resolve();
        return;
      }

      // gapi.load acepta un string y un callback o un objeto de configuración
      window.gapi.load('picker', () => {
        if (window.google?.picker) {
          resolve();
        } else {
          reject(new Error('Error al cargar Google Picker'));
        }
      });
    });
  }, []);

  // Obtener configuración del Picker desde el backend (deduped)
  const fetchPickerConfig = useCallback(async (retryCount = 0, force = false): Promise<PickerConfig | null> => {
    if (!token) return null;

    try {
      const data = await getGooglePickerConfig({ force: force || retryCount > 0 });
      setPickerConfig(data);
      setConfigError(null);
      return data;
    } catch (error: any) {
      console.error('Error fetching picker config:', error);
      
      // Si es error 401 y es el primer intento, reintentar después de un delay
      if (error.response?.status === 401 && retryCount < 3) {
        console.log(`Retrying picker config fetch (attempt ${retryCount + 1}/3)...`);
        await new Promise((r) => setTimeout(r, 1000 * (retryCount + 1)));
        return fetchPickerConfig(retryCount + 1, true);
      }
      
      const errorMessage = error.response?.data?.detail || 'Error al obtener configuración del Picker';
      setConfigError(errorMessage);
      
      // Si el error es de autenticación después de todos los reintentos, mostrar mensaje apropiado
      if (error.response?.status === 401) {
        toast.error('Necesitas conectar tu cuenta de Google primero');
      }
      return null;
    }
  }, [token]);

  const ensurePickerReady = useCallback(async (): Promise<PickerConfig | null> => {
    await loadGoogleApi();
    await loadPickerLibrary();
    if (pickerConfig) return pickerConfig;
    return fetchPickerConfig(0);
  }, [loadGoogleApi, loadPickerLibrary, pickerConfig, fetchPickerConfig]);

  // Crear y mostrar el Picker (lazy: scripts + config solo al hacer click)
  const openPicker = useCallback(async () => {
    setLoading(true);
    onPickerOpened?.();

    try {
      const config = await ensurePickerReady();
      if (!config || !window.google?.picker) {
        toast.error('El selector de archivos no está disponible. Intenta de nuevo.');
        setLoading(false);
        return;
      }

      const google = window.google;
      
      // Crear vista de Spreadsheets - mostrar archivos recientes primero, sin carpetas
      const view = new google.picker.DocsView(google.picker.ViewId.SPREADSHEETS);
      view.setMimeTypes(SPREADSHEET_MIME_TYPE);
      view.setIncludeFolders(false);  // No mostrar carpetas en la vista inicial
      view.setSelectFolderEnabled(false);  // Deshabilitar selección de carpetas
      if (window.innerWidth < 900) {
        view.setMode('list');
      }

      // Construir el Picker
      const pickerBuilder = new google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(config.access_token)
        .setDeveloperKey(config.api_key)
        .setAppId(config.app_id)
        .setTitle('Selecciona hojas de cálculo para conectar')
        .setLocale('es')
        .setCallback(async (data: GooglePickerResponse) => {
          if (data.action === google.picker.Action.PICKED && data.docs) {
            console.log('📊 Sheets selected via Picker:', data.docs);
            
            // Filtrar solo spreadsheets (por seguridad adicional)
            const spreadsheets = data.docs.filter(
              doc => doc.mimeType === SPREADSHEET_MIME_TYPE
            );

            if (spreadsheets.length === 0) {
              toast.warning('No seleccionaste ninguna hoja de cálculo válida');
              return;
            }

            // Registrar en el backend
            try {
              const response = await axios.post('/google-picker/sheets', {
                sheets: spreadsheets
              });

              if (response.data.success) {
                toast.success(`${response.data.total_registered} hoja(s) agregada(s) correctamente`);
                onSheetsSelected?.(spreadsheets);
              }
            } catch (error: any) {
              console.error('Error registering picked sheets:', error);
              toast.error('Error al registrar las hojas seleccionadas');
            }
          } else if (data.action === google.picker.Action.CANCEL) {
            console.log('Picker cancelled by user');
            onPickerClosed?.();
          }
          
          setLoading(false);
        });

      // Habilitar multiselección si está configurado
      if (multiSelect) {
        pickerBuilder.enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
      }

      // Habilitar soporte para Shared Drives
      pickerBuilder.enableFeature(google.picker.Feature.SUPPORT_DRIVES);

      // Construir y mostrar el Picker
      const picker = pickerBuilder.build();
      picker.setVisible(true);

      if (window.innerWidth < 900) {
        setTimeout(() => {
          const dialogs = Array.from(
            document.querySelectorAll('.picker-dialog')
          ) as HTMLElement[];

          const dialog =
            dialogs.find(
              (el) =>
                getComputedStyle(el).display !== 'none' &&
                el.getAttribute('aria-hidden') !== 'true'
            ) || null;

          const content = dialog?.querySelector(
            '.picker-dialog-content'
          ) as HTMLElement | null;

          if (dialog) {
            dialog.style.width = 'calc(100vw - 24px)';
            dialog.style.maxWidth = 'calc(100vw - 24px)';
            dialog.style.left = '50%';
            dialog.style.right = 'auto';
            dialog.style.transform = 'translateX(-50%)';
            dialog.style.margin = '0';
            dialog.style.boxSizing = 'border-box';
          }

          if (content) {
            content.style.width = '100%';
            content.style.maxWidth = '100%';
          }
        }, 100);
      }

    } catch (error) {
      console.error('Error opening picker:', error);
      toast.error('Error al abrir el selector de archivos');
      setLoading(false);
    }
  }, [ensurePickerReady, multiSelect, onPickerOpened, onPickerClosed, onSheetsSelected]);

  const autoOpenHandledRef = useRef(false);

  useEffect(() => {
    if (!autoOpen || autoOpenHandledRef.current || !token) {
      return;
    }

    autoOpenHandledRef.current = true;
    void openPicker();
  }, [autoOpen, token, openPicker]);

  // Renderizar estado de error de configuración
  if (configError) {
    return (
      <Button
        variant="outline"
        size={buttonSize}
        className={`${className} text-amber-600 border-amber-300`}
        onClick={() => {
          setConfigError(null);
          void fetchPickerConfig(0, true).then((cfg) => {
            if (cfg) void openPicker();
          });
        }}
      >
        <FolderOpen className="w-4 h-4 mr-2" />
        Reintentar conexión
      </Button>
    );
  }

  return (
    <Button
      variant={buttonVariant}
      size={buttonSize}
      className={className}
      onClick={() => void openPicker()}
      disabled={disabled || loading}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : showIcon ? (
        <Link2 className="w-4 h-4 mr-2" />
      ) : null}
      {loading ? 'Abriendo selector...' : buttonText}
    </Button>
  );
};

export default GoogleSheetsPicker;
